import React, { useState, useEffect } from 'react';
import { PrinterOutlined, CheckCircleOutlined, ExclamationCircleOutlined, DisconnectOutlined, ScanOutlined } from '@ant-design/icons';
import { Modal, Badge, Tooltip, Button } from 'antd';
import { useOnboard } from './OnboardContext';
import PrinterSelection from '../../components/PrinterSelection';
import BarcodeScannerModal from '../../components/BarcodeScannerModal';
import printerService from '../../services/printerService';
import './Footer.css';
import { useTheme } from '../../contexts/ThemeContext';

const Footer = ({ onOpenModal }) => {
  const [printerModalVisible, setPrinterModalVisible] = useState(false);
  const { printerConnected, printerName, printerType, setPrinterConnected, setPrinterName, setPrinterDevice, setPrinterEmulator, veiculoDoDia, buscaPath } = useOnboard();
  const [scannerVisible, setScannerVisible] = useState(false);
  const { currentTheme } = useTheme();
  const vehicleOpen = !!veiculoDoDia && !veiculoDoDia.dtFim;
  const canOperate = buscaPath === 'ssw' || vehicleOpen;
  const androidLeituraAtivaRef = React.useRef(false);
  const scanTimeoutRef = React.useRef(null);

  const toLocalYYYYMMDD = (date) => {
    try {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    } catch {
      return '';
    }
  };

  const hoje = new Date(); hoje.setHours(0,0,0,0);
  const dtFimDate = veiculoDoDia?.dtFim ? new Date(veiculoDoDia.dtFim) : null;
  const isClosedToday = !!veiculoDoDia?.dtFim && dtFimDate && (toLocalYYYYMMDD(dtFimDate) === toLocalYYYYMMDD(hoje));
  const buttonTooltipText = canOperate
    ? ''
    : (veiculoDoDia
        ? (isClosedToday ? 'Coletas encerradas para hoje' : 'Uso do veículo encerrado')
        : 'Selecione o veículo para iniciar');

  // Registrar handler global para leitura de código de barras
  useEffect(() => {
    window.onBarcodeRead = (code) => {
      try { androidLeituraAtivaRef.current = false; } catch {}
      try { if (scanTimeoutRef.current) { clearTimeout(scanTimeoutRef.current); scanTimeoutRef.current = null; } } catch {}
      if (!code) return;
      try {
        const evt = new CustomEvent('onBarcodeRead', { detail: { code } });
        window.dispatchEvent(evt);
      } catch (err) {
        console.warn('Falha ao despachar evento onBarcodeRead:', err);
      }
    };

    window.onBarcodeCancel = () => {
      try { androidLeituraAtivaRef.current = false; } catch {}
      try { if (scanTimeoutRef.current) { clearTimeout(scanTimeoutRef.current); scanTimeoutRef.current = null; } } catch {}
    };

    const handleCancelEvent = () => {
      try { androidLeituraAtivaRef.current = false; } catch {}
      try { if (scanTimeoutRef.current) { clearTimeout(scanTimeoutRef.current); scanTimeoutRef.current = null; } } catch {}
    };

    const handleReadEvent = () => {
      try { androidLeituraAtivaRef.current = false; } catch {}
      try { if (scanTimeoutRef.current) { clearTimeout(scanTimeoutRef.current); scanTimeoutRef.current = null; } } catch {}
    };

    window.addEventListener('onBarcodeCancel', handleCancelEvent);
    window.addEventListener('onBarcodeRead', handleReadEvent);

    return () => {
      try { delete window.onBarcodeRead; } catch {}
      try { delete window.onBarcodeCancel; } catch {}
      try { window.removeEventListener('onBarcodeCancel', handleCancelEvent); } catch {}
      try { window.removeEventListener('onBarcodeRead', handleReadEvent); } catch {}
    };
  }, []);

  const handlePrinterClick = () => {
    setPrinterModalVisible(true);
  };

  const iniciarScannerCodigoBarras = async () => {
    setScannerVisible(true);
  };

  const handlePrinterModalClose = () => {
    try {
      setPrinterModalVisible(false);
      setPrinterConnected(false);
      setPrinterName(null);
      setPrinterDevice(null);
      setPrinterEmulator(null);
      try { printerService.disconnect(); } catch {}
      try { (window && window.wsPrintClient ? window.wsPrintClient.close() : null); } catch {}
    } catch {}
  };

  const ler = async () => {
    if (!canOperate) {
      return;
    }

    try {
      const isAndroid = typeof window !== 'undefined' && !!window.Android;
      if (isAndroid) {
        if (androidLeituraAtivaRef.current) { return; }
        if (typeof window.Android?.ler === 'function') {
          androidLeituraAtivaRef.current = true;
          window.Android.ler();
          try { if (scanTimeoutRef.current) { clearTimeout(scanTimeoutRef.current); } scanTimeoutRef.current = setTimeout(() => { androidLeituraAtivaRef.current = false; scanTimeoutRef.current = null; }, 15000); } catch {}
        } else {
          await iniciarScannerCodigoBarras();
        }
      } else {
        await iniciarScannerCodigoBarras();
      }
    } catch (e) {
      // substitui showAlertWarn por mensagem no console/antd caso necessário
      console.warn('Only for android devices\n' + (e?.message || String(e)));
    }
  };

  const handleOpenModalClick = (e) => {
    if (!canOperate) { return; }
    try { if (typeof onOpenModal === 'function') { onOpenModal(); } } catch {}
    try { e && e.currentTarget && e.currentTarget.blur && e.currentTarget.blur(); } catch {}
  };

  const desconectarImpressora = async () => {
    try {
      await printerService.disconnect();
      setPrinterConnected(false);
      setPrinterName(null);
      setPrinterDevice(null);

      try {
        localStorage.removeItem('printerName');
        localStorage.removeItem('printerType');
      } catch (e) { /* ignore */ }

      if (typeof window !== 'undefined' && window.Android && typeof window.Android.desconectar === 'function') {
        window.Android.desconectar();
      }
    } catch (err) {
      // Silenciar erro aqui; PrinterSelection lida com mensagens
      console.error('Erro ao desconectar impressora:', err);
    }
  };

  const getPrinterStatus = () => {
    if (printerConnected) {
      return {
        icon: <CheckCircleOutlined />, color: '#52c41a', status: 'success', text: `Conectada: ${printerName || 'Impressora'}`
      };
    }
    if (printerName) {
      return {
        icon: <DisconnectOutlined />, color: '#ff4d4f', status: 'error', text: `Desconectada: ${printerName} (clique para conectar)`
      };
    }
    if (printerType) {
      return {
        icon: <ExclamationCircleOutlined />, color: '#faad14', status: 'warning', text: `Tipo selecionado: ${printerType} (desconectada)`
      };
    }
    return { icon: <DisconnectOutlined />, color: '#8c8c8c', status: 'default', text: 'Nenhuma impressora configurada' };
  };

  const printerStatus = getPrinterStatus();

  return (
    <footer className={`footer-appbar ${currentTheme === 'dark' ? 'theme-dark' : 'theme-light'}`}>
      <div className="footer-toolbar">
        {/* Elemento à esquerda (nome da impressora) */}
        <div className="footer-item text-left">
            {printerName && (
              <span className="footer-printer-name" style={{ color: printerConnected ? 'inherit' : '#8c8c8c' }}>
                {printerName} {!printerConnected ? '(Desconectada)' : ''}
              </span>
            )}
        </div>

        <div className="footer-book text-center">
          <div className="footer-book-fab-container"></div>
          <Tooltip title={buttonTooltipText} placement="top">
            <button
              className={`footer-fab ${!canOperate ? 'disabled' : ''}`}
              type="button"
              onClick={handleOpenModalClick}
              aria-label="Ler Código de Barras"
              disabled={!canOperate}
            >
                <ScanOutlined style={{ fontSize: 20, color: currentTheme === 'dark' ? '#ffffff' : undefined }} />
            </button>
          </Tooltip>
        </div>

        {/* Status e ações da impressora à direita */}
        <div className="footer-printer-container">
          <Tooltip title={printerStatus.text} placement="top">
            <div className={`footer-printer-small ${!printerConnected && !printerType ? 'no-printer' : ''}`} onClick={handlePrinterClick}>
              <Badge dot status={printerStatus.status}>
            <PrinterOutlined style={{ fontSize: 20 }} />
              </Badge>
            </div>
          </Tooltip>
          {printerConnected && (
            <Button type="link" size="small" onClick={desconectarImpressora} style={{ color: '#ff4d4f' }}>
              Desconectar
            </Button>
          )}
        </div>
      </div>

      <Modal
        title="Selecionar Impressora"
        open={printerModalVisible}
        onCancel={handlePrinterModalClose}
        footer={null}
        width={600}
        centered 
        maskClosable = {false} 
      >
        <PrinterSelection 
          onPrinterConnect={() => setPrinterModalVisible(false)}
        />
      </Modal>

      <BarcodeScannerModal
        visible={scannerVisible}
        onDetected={(decodedText) => {
          if (decodedText) {
            try {
              const evt = new CustomEvent('onBarcodeRead', { detail: { code: decodedText } });
              window.dispatchEvent(evt);
            } catch {}
          }
        }}
        onClose={() => setScannerVisible(false)}
      />
    </footer>
  );
};

export default Footer;